import { useEffect, useState, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import DataList from '../../common/components/DataList';
import { Role } from '../../common/utils/loginRole';
import {
  maximumImportFileUpload,
} from '../../common/utils/validationUtils';
import { ClinicAccountItems } from '../../types/ClinicAccountType';
import { ResponseError } from '../../types/ResponseError';
import {
  ErrorDataImportUser,
  ImportUserResponse,
} from '../../types/UserTypes';
import { selectUser } from '../auth/authSlice';
import { accountApis } from '../../app/services/account';
import { UploadUrlResponse } from '../../types/UploadUrl';
import axios from 'axios';
import { useToast } from '../../app/toast';
import { ImportFolderType } from '../../common/utils/importFolder';

interface ImportUserFormProps {
  siteData?: any;
  clinicAccounts?: ClinicAccountItems[];
  onFormSubmit: (successCallback?: () => void) => Promise<void>;
}

const StyleAutocomplete = styled(Autocomplete)(({ theme }) => ({
  width: '49%',
  '& .MuiOutlinedInput-root': {
    padding: '10px',
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: theme.xPalette.lightGrey,
    fontWeight: theme.typography.h1.fontWeight,
    backgroundColor: 'rgb(254, 239, 238)',
    borderBottom: 0,
    fontSize: 16,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 16,
    fontWeight: theme.typography.h4.fontWeight,
  },
}));

export default function ImportForm({
  siteData,
  clinicAccounts,
  onFormSubmit,
}: ImportUserFormProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));
  const [pageErrors, setPageErrors] = useState<string[]>([]);
  const inputImportUser = useRef<HTMLInputElement>(null);
  const [userFile, setUserFile] = useState<any>(null);
  const [dataFile, setDataFile] = useState<string>('');
  const [dataClinicAccount, setDataClinicAccount] = useState<string>('');
  const [isShowErrorAccount, setIsShowErrorAccount] = useState<boolean>(false);
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const currentUser = useAppSelector(selectUser);
  const [errorData, setErrorData] = useState<ErrorDataImportUser[]>([]);
  const [importFileName, setImportFileName] = useState<string>('');
  const dispatch = useAppDispatch();

  const tableHeaders: string[] = Object.values(
    t('user.manageUsers.importUserTableHeader', {
      returnObjects: true,
    })
  );

  const { control, handleSubmit, setValue } = useForm<ImportUserResponse>({});

  const handleImportUser = () => {
    if (!dataClinicAccount) {
      setIsShowErrorAccount(true);
    } else {
      inputImportUser?.current?.click();
    }
  };

  const filterAccountOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option: any) => getAccountOptionLabel(option),
  });

  const getAccountOptionLabel = (option: any) => `${option.name} (${option.id})`;

  useEffect(() => {
    setPageErrors([]);
    setErrorData([]);
    if (userFile) {
      if (userFile && !dataClinicAccount) {
        setPageErrors([t('user.manageUsers.error.blankClinicAccount')]);
      }
      const read = new FileReader();
      read.readAsBinaryString(userFile);
      setImportFileName(userFile.name);
      if (userFile.type && !userFile.type.includes('csv') && !userFile.type.includes('excel')) {
        setPageErrors([t('user.manageUsers.error.invalidFileFormat')]);
        return;
      }
      read.onloadend = () => {
        setDataFile(read.result as string);
      };
    }
  }, [userFile, t, dataClinicAccount]);

  useEffect(() => {
    if (currentUser.roleId !== Role.SuperAdmin && clinicAccounts) {
      setValue('clinicAccount', clinicAccounts[0].id);
      setDataClinicAccount(clinicAccounts[0].id);
    }
  }, [clinicAccounts, currentUser.roleId, setValue]);

  const handleAccountChange = (e: any, values: any) => {
    const selectedValue = values ? values.id : '';
    setValue('clinicAccount', selectedValue);
    setDataClinicAccount(selectedValue);
    setPageErrors([]);
    setErrorData([]);
    if (!selectedValue) {
      setIsShowErrorAccount(true);
    } else {
      setIsShowErrorAccount(false);
    }
  };

  const onSubmit = async (data: ImportUserResponse) => {
    setPageErrors([]);
    setErrorData([]);
    let pageErrors: string[] = [];

    if (userFile && userFile.size / (1024 * 1024) > maximumImportFileUpload) {
      pageErrors.push(
        t('user.manageUsers.error.importFileLargeThanMaximum', {
          maximumFileUpload: maximumImportFileUpload,
        })
      );
    }
    if (!dataFile) {
      pageErrors.push(t('user.manageUsers.error.blankImportFile'));
    }
    if (!data.clinicAccount) {
      pageErrors.push(t('user.manageUsers.error.blankAccount'));
    }

    if (pageErrors.length) {
      setPageErrors(pageErrors);
      return;
    }
    try {
      // Get presigned URL from S3
      const uploadUrlResult = await dispatch(
        accountApis.getUploadUrl.initiate({
          clinicAccountId: data.clinicAccount,
          fileName: userFile.name,
          contentType: userFile.type,
          key: ImportFolderType.User,
        }, { forceRefetch: true })
      );

      const uploadUrlData: UploadUrlResponse | undefined = uploadUrlResult?.data;

      if (!uploadUrlData || !uploadUrlData.url || !uploadUrlData.key) {
        throw new Error('Failed to get upload URL');
      }

      const { url } = uploadUrlData;

      // Upload file to S3
      axios.put(url, userFile, {
        headers: {
          'Content-Type': userFile.type,
        },
        withCredentials: false,
        timeout: 10000,
      });
      toast.publish(t('user.manageUsers.processingMessage'), 'success');
      await onFormSubmit();
      setPageErrors([]);

    } catch (e) {
      const {
        data: { error },
      } = e as ResponseError;
      setPageErrors(error);
    }
  };

  const renderDesktopData = (data: ErrorDataImportUser[]) => {
    return (
      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '16px 16px 0 0' }}>
        <Table>
          <TableHead>
            <TableRow>
              {tableHeaders.map((header, index) => {
                return <StyledTableCell key={`${header} ${index}`}>{header}</StyledTableCell>;
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item: ErrorDataImportUser, index) => {
              return (
                <TableRow key={index}>
                  <StyledTableCell sx={{ wordBreak: 'break-all', minWidth: '300px' }}>
                    {item.line}
                  </StyledTableCell>
                  <StyledTableCell sx={{ wordBreak: 'break-word', minWidth: '160px' }}>
                    {item.errorDescription}
                  </StyledTableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderMobileData = (data: ErrorDataImportUser[]) => {
    return (
      <DataList
        lineHeight={'auto'}
        data={data.map((item: ErrorDataImportUser) => {
          const listItems = [
            [<>{t('user.manageUsers.importUserTableHeader.line')}</>, <>{item.line}</>],
            [
              <>{t('user.manageUsers.importUserTableHeader.errorDescription')}</>,
              <Box sx={{ wordBreak: 'break-word' }}>{item.errorDescription}</Box>,
            ],
          ];
          return {
            leftHeader: (
              <Typography variant="body1" fontWeight="bold" fontSize={20}>
                {`${t('user.manageUsers.importUserTableHeader.line')} ${item.line}`}
              </Typography>
            ),
            rightHeader: <p></p>,
            items: [...listItems],
          };
        })}
        customStyle={{
          borderRadius: '16px 16px 0 0',
        }}
      />
    );
  };

  return (
    <Box
      component="form"
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        px: matches ? 0 : 3,
        pt: 3,
      }}
    >
      <input
        type="file"
        id="file"
        ref={inputImportUser}
        accept=".csv"
        onClick={(event: React.MouseEvent<HTMLInputElement>) => {
          const element = event.target as HTMLInputElement;
          element.value = '';
        }}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          if (event?.target?.files) {
            setUserFile(event.target.files[0]);
          }
        }}
        style={{ display: 'none' }}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        {!(
          currentUser.roleId === Role.Interviewer ||
          currentUser.roleId === Role.PsychiatricConsult
        ) && (
          <Controller
            name="clinicAccount"
            control={control}
            render={({ fieldState, formState, field: { value }, ...props }) => (
              <StyleAutocomplete
                style={{ width: isDesktop ? '78%' : '100%' }}
                {...props}
                data-testid="mui-component-select-clinic-account"
                options={clinicAccounts || []}
                getOptionLabel={getAccountOptionLabel}
                onChange={handleAccountChange}
                value={value ? clinicAccounts?.find((item) => item.id === value) || null : null}
                filterOptions={filterAccountOptions}
                renderOption={(props, option: any) => {
                  return (
                    <li {...props} key={option.id} value={option.id}>
                      {getAccountOptionLabel(option)}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="normal"
                    fullWidth
                    label={t('user.manageUsers.userForm.account')}
                    error={isShowErrorAccount}
                    helperText={
                      isShowErrorAccount ? t('user.manageUsers.error.blankClinicAccount') : ''
                    }
                  />
                )}
              />
            )}
          />
        )}

        <Controller
          name="importFile"
          control={control}
          render={() => (
            <Button
              type="button"
              onClick={handleImportUser}
              fullWidth
              variant="contained"
              sx={{
                width: isDesktop ? '20%' : '100%',
                height: isDesktop ? '60px' : '100%',
                my: 2,
                py: 2,
                float: 'right',
              }}
            >
              {t('user.manageUsers.importUserForm.selectFile')}
            </Button>
          )}
        />
        <div style={isDesktop ? { marginLeft: '0%' } : { width: '100%', float: 'right' }}>
          {importFileName && `${t('user.manageUsers.fileName')}: ${importFileName}`}
        </div>
      </Box>
      <Box>
        {pageErrors?.length > 0 &&
          pageErrors.map((error) => (
            <Alert key={error} severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          ))}

        {errorData.length
          ? isDesktop
            ? renderDesktopData(errorData)
            : renderMobileData(errorData)
          : null}

        {!pageErrors.length && !errorData.length && dataFile && dataClinicAccount && (
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              width: isDesktop ? '20%' : '100%',
              my: 2,
              py: 2,
              float: 'left',
            }}
          >
            {t('user.manageUsers.importUserForm.import')}
          </Button>
        )}
      </Box>
    </Box>
  );
}
